import type { PopupWithCampaignResponse } from '@coorum-research/shared-common/api-response/popup.schema'
import { LauncherPopup } from '@coorum-research/core/src/components/LauncherPopup'
import { useOnceCall } from '@coorum-research/core/src/composables/use-once-call'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useQuery } from '@tanstack/react-query'
import { use, useRef, useState } from 'react'
import { popupApi } from '../apis/popup.api'
import { useHomeLauncher } from '../composables/use-home-launcher'
import { usePopupSuspends } from '../composables/use-popup-suspends'
import { renderResearchPage } from '../utils/RenderResearchPage'
import { recordUserActionHistory } from '../utils/user-action-history.util'
import { featureFlags } from './feature-flag/feature-flag.config'
import { HomeLauncherItemsContainer, HomeLauncherTriggerContainer } from './HomeLauncherContainer'
import { SessionContext } from './SessionProvider'

export function LauncherPopupContainer({ popup, onClose }: {
  popup: PopupWithCampaignResponse
  onClose?: (popup: PopupWithCampaignResponse) => void
}) {
  const [showPopup, setShowPopup] = useState(true)
  const ref = useRef<HTMLDivElement | null>(null)

  // データ計測 (popupView)
  // fixme: LauncherContainerからmapルーブでポップアップの数だけAPIリクエストが飛ぶので、1リクエストでまとめて送るように改修する
  const isUserActionHistoriesEnabled = useBooleanFlagValue(featureFlags.ACCOUNT_USER_ACTION_HISTORIES, false)

  useOnceCall(() => {
    if (isUserActionHistoriesEnabled) {
      recordUserActionHistory({
        actionType: 'popupView',
        popupId: popup.id,
        popupName: popup.name,
      }).catch((error) => {
        console.error('recordUserActionHistory popupView error', error)
      })
    }
  })

  const handleClick = () => {
    renderResearchPage({
      show: true,
      popup,
      onShow: () => setShowPopup(false),
      // 閉じるボタンを押した際はポップアップは再表示しない(boot or ブラウザreloadで再表示)
      // TODO: 完了したキャンペーンをLauncher/CoorumResearch間でstate管理して、onClose時にポップアップ再表示可能かcheckする
      //       + 複数キャンペーン選択画面で完了したキャンペーンは表示しないよう改修する
      onClose: () => setShowPopup(true),
    })
  }

  const handleClose = () => {
    setShowPopup(false)

    if (popup && onClose) {
      onClose(popup)
    }
  }

  return (
    <>
      {showPopup && (
        <LauncherPopup
          ref={ref}
          configJson={popup.configJson}
          onClick={handleClick}
          onClose={handleClose}
        />
      )}
    </>
  )
}

export function LauncherContainer() {
  const { session } = use(SessionContext)

  const { bootListPopup } = popupApi()

  // Popups
  const { data: popups } = useQuery({
    queryKey: ['get-available-popups'],
    queryFn: () => bootListPopup(),
    refetchOnWindowFocus: false,
  })

  // 表示しないPopupsの制御
  const { popupsOpen, popupsClosed, popupsReady, appendSuspendRecord } = usePopupSuspends(popups)
  const handlePopupClose = (popup: PopupWithCampaignResponse) => {
    appendSuspendRecord(popup.id)
  }

  // HomeLauncher
  const { homeLauncher, showHomeLauncher } = useHomeLauncher({ enabled: popupsReady, popupsClosed })

  return (
    <>
      {homeLauncher && showHomeLauncher && <HomeLauncherTriggerContainer homeLauncher={homeLauncher} />}
      {session && popupsOpen && (popupsOpen.length > 0) && popupsOpen.map(popup => (
        <LauncherPopupContainer
          key={popup.id}
          popup={popup}
          onClose={handlePopupClose}
        />
      ))}
      {homeLauncher && showHomeLauncher && <HomeLauncherItemsContainer homeLauncher={homeLauncher} />}
    </>
  )
}
